import { useId, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { LanguagePillSwitcher } from '@/shared/ui/language-pill-switcher'
import { RadioGroup } from '@/shared/ui/radio-group'
import { Textarea } from '@/shared/ui/textarea'
import { TicketTypeOption } from './TicketTypeOption'
import type { TicketConfigFormValues } from './schema'

const LANGUAGES: Array<'en' | 'vi-south'> = ['en', 'vi-south']

export function BasicInfoSection() {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const [activeLanguage, setActiveLanguage] = useState<'en' | 'vi-south'>('en')
  const {
    setValue,
    watch,
    control,
    formState: { errors, submitCount },
  } = useFormContext<TicketConfigFormValues>()
  const nameInputId = useId()
  const descriptionInputId = useId()

  const ticketType = watch('ticketType')
  const enName = useWatch({ control, name: 'localizedData.en.name' })
  const viName = useWatch({ control, name: 'localizedData.vi-south.name' })
  const enDescription = useWatch({ control, name: 'localizedData.en.description' })
  const viDescription = useWatch({ control, name: 'localizedData.vi-south.description' })

  const activeName = activeLanguage === 'en' ? enName : viName
  const activeDescription = activeLanguage === 'en' ? enDescription : viDescription

  const completedLanguages = useMemo(() => {
    return LANGUAGES.filter((language) => {
      const name = language === 'en' ? enName : viName
      return name?.trim().length > 0
    })
  }, [enName, viName])

  const languageLabels = useMemo(
    () =>
      Object.fromEntries(
        LANGUAGES.map((language) => [language, languageLabel(language)]),
      ) as Record<'en' | 'vi-south', string>,
    [languageLabel],
  )

  const activeNameError = errors.localizedData?.[activeLanguage]?.name?.message
    ? t(errors.localizedData[activeLanguage].name.message)
    : undefined

  // * VI: Lỗi bắt buộc có thể nằm ở tab ngôn ngữ đang ẩn; sau mỗi lần submit nhảy sang tab đó để người dùng thấy lỗi | EN: A required-name error can sit on the hidden language tab; after each submit jump to the tab that has it so the error is visible
  const errorLanguage = LANGUAGES.find((language) => errors.localizedData?.[language]?.name)
  const [handledSubmitCount, setHandledSubmitCount] = useState(0)
  // Adjusted during render (not in an effect): only reacts to a new submit, so switching tabs by hand
  // is never undone while another tab still has an error.
  if (submitCount !== handledSubmitCount) {
    setHandledSubmitCount(submitCount)
    if (errorLanguage && !errors.localizedData?.[activeLanguage]?.name) {
      setActiveLanguage(errorLanguage)
    }
  }

  function handleNameChange(value: string) {
    setValue(`localizedData.${activeLanguage}.name`, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  function handleDescriptionChange(value: string) {
    setValue(`localizedData.${activeLanguage}.description`, value, {
      shouldDirty: true,
    })
  }

  return (
    <div className="space-y-6">
      <FormField
        label={t('ticketConfig.ticketType', { defaultValue: 'Ticket Type' })}
        required
        error={errors.ticketType?.message ? t(errors.ticketType.message) : undefined}
      >
        <RadioGroup
          value={ticketType}
          onValueChange={(value) => setValue('ticketType', value as 'free' | 'paid')}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <TicketTypeOption
            value="free"
            label={t('ticketConfig.free', { defaultValue: 'Free' })}
            description={t('ticketConfig.freeHint', {
              defaultValue: 'No payment required.',
            })}
          />
          <TicketTypeOption
            value="paid"
            label={t('ticketConfig.paid', { defaultValue: 'Paid' })}
            description={t('ticketConfig.paidHint', {
              defaultValue: 'Requires payment processing.',
            })}
          />
        </RadioGroup>
      </FormField>

      <div className="space-y-4 rounded-xl border border-zinc-200 bg-zinc-50/50 p-4">
        <LanguagePillSwitcher
          languages={LANGUAGES}
          activeLanguage={activeLanguage}
          onSelect={(language) => setActiveLanguage(language as 'en' | 'vi-south')}
          completedLanguages={completedLanguages}
          requiredLanguage="en"
          languageLabels={languageLabels}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={t('common.name')} required error={activeNameError} id={nameInputId}>
            <Input
              id={nameInputId}
              value={activeName ?? ''}
              onChange={(event) => handleNameChange(event.target.value)}
              placeholder={t('ticketConfig.namePlaceholder', {
                defaultValue: 'Enter ticket config name',
              })}
            />
          </FormField>

          <FormField
            label={t('common.description', { defaultValue: 'Description' })}
            optional
            id={descriptionInputId}
          >
            <Textarea
              id={descriptionInputId}
              value={activeDescription ?? ''}
              onChange={(event) => handleDescriptionChange(event.target.value)}
              rows={3}
              className="resize-none"
              placeholder={t('ticketConfig.descriptionPlaceholder', {
                defaultValue: 'Enter description',
              })}
            />
          </FormField>
        </div>
      </div>
    </div>
  )
}
